/**
 * Which click targets the canvas shows while an SME marks, and which one a
 * click lands on. Pure functions over sheet-pixel boxes, so the workspace can
 * memoize each step on its own inputs: retirement changes when a marking is
 * saved or deleted, and containment only when the visible list does.
 *
 * Apply them in order: `retireUnderSavedRegions`, then
 * `hideContainedClickTargets`, then `clickTargetAt` on what is left. Retiring
 * first matters: a target already marked must not hide a smaller one inside it
 * that the SME can still click.
 */
import type { BoundingBox, ClickTarget } from '../types';
import { bboxArea, isPointInBbox, type Point } from '../utils/geometry';

/**
 * The targets whose centre lies outside every saved region.
 *
 * A target centred in a saved region is already marked. It comes back when that
 * marking is deleted, because the target itself is never changed.
 */
export function retireUnderSavedRegions(
  clickTargets: ClickTarget[],
  savedRegions: BoundingBox[],
): ClickTarget[] {
  return clickTargets.filter(({ bbox }) => {
    const centre = { x: (bbox.x1 + bbox.x2) / 2, y: (bbox.y1 + bbox.y2) / 2 };
    return !savedRegions.some((region) => isPointInBbox(centre, region));
  });
}

function containsBox(outer: BoundingBox, inner: BoundingBox): boolean {
  return (
    outer.x1 <= inner.x1 && outer.y1 <= inner.y1 && outer.x2 >= inner.x2 && outer.y2 >= inner.y2
  );
}

// Any box that contains another sorts before it. Equal boxes keep their input
// order, so the first of two identical targets is the one that stays.
function containersFirst(first: ClickTarget, second: ClickTarget): number {
  return (
    first.bbox.x1 - second.bbox.x1 ||
    second.bbox.x2 - first.bbox.x2 ||
    first.bbox.y1 - second.bbox.y1 ||
    second.bbox.y2 - first.bbox.y2
  );
}

/**
 * The targets that sit inside no other target of the list, in input order.
 *
 * A click on the outer box crops the inner one with it, so the inner target adds
 * nothing to click. The sweep visits targets by left edge and compares each only
 * with the shown targets whose right edge still reaches it, so the cost stays
 * near O(n log n) on a sheet of small, scattered boxes.
 */
export function hideContainedClickTargets(clickTargets: ClickTarget[]): ClickTarget[] {
  const hidden = new Set<ClickTarget>();
  let reachingContainers: ClickTarget[] = [];
  for (const clickTarget of [...clickTargets].sort(containersFirst)) {
    const { bbox } = clickTarget;
    // A box that ends left of this one cannot contain it, nor any box after it.
    reachingContainers = reachingContainers.filter((container) => container.bbox.x2 >= bbox.x1);
    if (reachingContainers.some((container) => containsBox(container.bbox, bbox))) {
      hidden.add(clickTarget);
    } else {
      reachingContainers.push(clickTarget);
    }
  }
  return clickTargets.filter((clickTarget) => !hidden.has(clickTarget));
}

/** The largest target whose box contains `point`, or null when none does. */
export function clickTargetAt(clickTargets: ClickTarget[], point: Point): ClickTarget | null {
  let largest: ClickTarget | null = null;
  for (const clickTarget of clickTargets) {
    if (!isPointInBbox(point, clickTarget.bbox)) continue;
    if (largest === null || bboxArea(clickTarget.bbox) > bboxArea(largest.bbox)) {
      largest = clickTarget;
    }
  }
  return largest;
}
